import type { AppNode, AppNodeOf, NodeKind } from '@/flow/types';
import { getNodeDisplayLabel } from '@/flow/displayLanguage';
import type { LearningGoal, LearningProblem, LearningRunEvent } from './learningTypes';

export type LearningEvaluation = {
  status: 'correct' | 'incorrect' | 'error';
  feedback: string[];
};

const countFinished = (events: LearningRunEvent[], nodeKind: NodeKind) => events.filter((event) => (
  event.type === 'node-finished' && event.nodeKind === nodeKind
)).length;

const evaluateFunction = (
  goal: Extract<LearningGoal, { type: 'functionDefinition' }>,
  nodes: AppNode[],
  events: LearningRunEvent[],
) => {
  const functionNode = nodes.find((node): node is AppNodeOf<'function'> => (
    node.type === 'function' && node.data.functionName.trim() === goal.name
  ));
  if (!functionNode) return false;
  const internalKinds = new Set(functionNode.data.internalNodes.map(({ type }) => type));
  return goal.requiredNodeKinds.every((kind) => internalKinds.has(kind))
    && events.filter((event) => event.type === 'node-finished' && event.nodeId === functionNode.id).length >= goal.minimumCalls;
};

export const evaluateLearningRun = (
  problem: LearningProblem,
  nodes: AppNode[],
  events: LearningRunEvent[],
): LearningEvaluation => {
  const runtimeError = events.find((event) => event.type === 'run-error');
  if (runtimeError?.type === 'run-error') return { status: 'error', feedback: [runtimeError.message] };

  const feedback = problem.goals.flatMap((goal): string[] => {
    if (goal.type === 'terminalReached') {
      return events.some((event) => event.type === 'terminal-reached')
        ? []
        : [`フローが${getNodeDisplayLabel('end', 'ja')}まで進みませんでした。`];
    }
    if (goal.type === 'nodeExecuted') {
      const actual = countFinished(events, goal.nodeKind);
      return actual >= (goal.minimumCount ?? 1)
        ? []
        : [`${getNodeDisplayLabel(goal.nodeKind, 'ja')}カードを実行してみましょう。`];
    }
    if (goal.type === 'speech') {
      const matches = events.filter((event) => event.type === 'speech' && (
        goal.match === 'contains' ? event.text.includes(goal.text) : event.text === goal.text
      ));
      return matches.length >= (goal.minimumCount ?? 1) ? [] : [`「${goal.text}」と話せていません。`];
    }
    if (goal.type === 'goalReached') {
      return events.some((event) => event.type === 'goal-reached') ? [] : ['ゴールにはまだ届いていません。'];
    }
    if (goal.type === 'noCollision') {
      return events.some((event) => event.type === 'collision') ? ['かべにぶつかりました。'] : [];
    }
    return evaluateFunction(goal, nodes, events) ? [] : [`Function「${goal.name}」の中身と呼び出し回数を確認しましょう。`];
  });
  return feedback.length === 0 ? { status: 'correct', feedback: [] } : { status: 'incorrect', feedback };
};

export const shouldShowOptimalAlternative = (
  problem: LearningProblem,
  nodes: AppNode[],
  events: LearningRunEvent[],
) => {
  const optimal = problem.optimalSolutions[0];
  if (!optimal) return false;
  const actualKinds = nodes.filter(({ type }) => type !== 'start' && type !== 'end').map(({ type }) => type).sort();
  const optimalKinds = [...optimal.nodeKinds].sort();
  const executedCount = events.filter(({ type }) => type === 'node-finished').length;
  return executedCount > optimal.expectedExecutedNodeCount
    || actualKinds.length !== optimalKinds.length
    || actualKinds.some((kind, index) => kind !== optimalKinds[index]);
};